// src/pages/BookingBoard.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import { shouldShowSeatBookingInTodayList } from '../utils/seatTodayListRules';
axios.defaults.withCredentials = true;

/** === 可調整參數 === */
const REFRESH_MS = 5000;          // 定期輪詢間隔（5秒，無論 socket 是否連接都執行）
const TAB_MAX_NAME_W = 220;       // 分頁標籤名稱最大寬度（會省略號）

/** 二樓「各分區」定義（依前四字 KEY4） */
const SECTION_NAME_MAP = {
  '1F-C': '1F咖啡廳座位',
  '2F-H': '2F-H 開放區高腳座位',
  '2F-L': '2F-L 開放區升降桌座',
  '2F-P': '2F-P 開放區面牆位',
  '2F-Q': '2F-Q 專注區大桌座位',
  '2F-W': '2F-W 專注區面窗座位',
  '夜貓子方案': '夜貓子方案', // 特殊分區
};
const TWO_F_SECTIONS = ['2F-H', '2F-L', '2F-P', '2F-Q', '2F-W'];

/** 會員等級徽章 */
const LevelBadge = ({ level }) => {
  if (!level) return null;
  const color =
    /資深/.test(level) ? '#7c3aed' :
    (/年會員|月會員/.test(level) ? '#2563eb' : '#6b7280');
  return (
    <span style={{
      marginLeft: 8, padding: '2px 8px', fontSize: 12, fontWeight: 800,
      borderRadius: 999, background: color, color: '#fff', whiteSpace: 'nowrap'
    }}>
      {level}
    </span>
  );
};

/** 工具 */
const pickHM = (v) => {
  if (!v) return '-';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2,'0')}:${m[2]}` : '-';
};
const yyyyMMdd = (d) => {
  const y = d.getFullYear();
  const m = `${d.getMonth()+1}`.padStart(2, '0');
  const dd = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const parseDate = (s) => {
  const m = String(s||'').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
const key4Of = (seatName) => String(seatName || '').trim().slice(0, 4);
const isSeatOnFloor = (seatName, floor) => {
  if (!floor || floor === 'ALL') return true;
  const k4 = key4Of(seatName).toUpperCase();
  if (floor === '1F') return k4.startsWith('1F');
  if (floor === '2F') return k4.startsWith('2F');
  return true;
};

export default function BookingBoard(){
  // 後端 sections 仍保留抓取（提供參考統計），不作切頁依據
  const [sections, setSections] = useState([]);
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [day, setDay]           = useState(() => yyyyMMdd(new Date())); // 預設今日

  // 上方分頁目前選擇：ALL / 1F / 2F / 2F-H / 2F-L / 2F-P / 2F-Q / 2F-W
  const [view, setView] = useState('ALL');

  // socket
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // 掛上 --vh（行動瀏覽器 100vh 修正）
  useEffect(() => {
    const setVH = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // 關閉外層捲動 & 去掉 body 預設 margin，避免出現外部滾動條
  useEffect(() => {
    const prevMargin = document.body.style.margin;
    const prevOverflowBody = document.body.style.overflow;
    const prevOverflowHtml = document.documentElement.style.overflow;
    document.body.style.margin = '0';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.margin = prevMargin;
      document.documentElement.style.overflow = prevOverflowHtml;
      document.body.style.overflow = prevOverflowBody;
    };
  }, []);

  const mountedRef = useRef(true);
  const scrollRef = useRef(null);

  // 捲動保留（避免刷新跳頂）
  const preserveScroll = (fn) => async (...args) => {
    const box = scrollRef.current;
    const top = box ? box.scrollTop : 0;
    try { await fn(...args); }
    finally { if (box) requestAnimationFrame(() => { box.scrollTop = top; }); }
  };

  const dayLabel = useMemo(() => {
    const d = parseDate(day);
    return `${d.getMonth()+1}/${d.getDate()}（${'日一二三四五六'[d.getDay()]}）`;
  }, [day]);

  /** 讀 sections（保留參考統計） */
  const fetchSections = preserveScroll(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get('/api/bookings/sections', { params:{ day } });
      if (!mountedRef.current) return;
      setSections(data?.sections || []);
    } catch (e) {
      if (!mountedRef.current) return;
      console.warn(e);
      if (!silent) setErr('讀取失敗');
    } finally {
      if (!silent && mountedRef.current) setLoading(false);
    }
  });

  /** 讀整日清單（不分分區；之後用 view 過濾） */
  const fetchList = preserveScroll(async ({ d = day, silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get('/api/bookings/today', { params:{ section: '', day: d } });
      if (!mountedRef.current) return;
      const raw = Array.isArray(data?.list) ? data.list : [];
      setList(raw.filter(shouldShowSeatBookingInTodayList));
    } catch (e) {
      if (!mountedRef.current) return;
      console.warn(e);
      if (!silent) setErr('讀取失敗');
    } finally {
      if (!silent && mountedRef.current) setLoading(false);
    }
  });

  // 初始化 & 切日
  useEffect(() => {
    mountedRef.current = true;
    fetchSections({ silent: true });
    fetchList({ d: day, silent: false });
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  /** Socket：以 day 入房；改日就換房 */
  useEffect(() => {
    const s = socketIO('/', { path: '/socket.io' });
    socketRef.current = s;

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);

    // 更新：有 row 就合併單列，否則整批 silent refresh
    const onUpdate = ({ row }) => {
      if (!mountedRef.current) return;
      if (row) {
        setList((ls) => {
          const exists = ls.some(x => x.ID === row.ID);
          const oldRow = exists ? ls.find(x => x.ID === row.ID) : null;
          const merged = exists && oldRow ? { ...oldRow, ...row } : row;
          if (!shouldShowSeatBookingInTodayList(merged)) {
            return ls.filter((x) => x.ID !== row.ID);
          }
          const numberChanged = oldRow && (Number(oldRow.NUMBER) || 0) !== (Number(row.NUMBER) || 0);
          const updated = exists
            ? ls.map((x) => (x.ID === row.ID ? merged : x))
            : [...ls, merged];
          if (numberChanged) {
            setTimeout(() => {
              fetchSections({ silent: true });
            }, 0);
          }
          return updated;
        });
      } else {
        fetchList({ d: day, silent: true });
      }
    };
    const onUpdateSections = ({ day: eventDay }) => {
      if (!mountedRef.current) return;
      // 只有當事件日期與當前日期匹配時才更新
      if (!eventDay || eventDay === day) {
        fetchSections({ silent: true });
        // 同時更新列表，確保數據同步
        fetchList({ d: day, silent: true });
      }
    };

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('bookings:update', onUpdate);
    s.on('bookings:update-sections', onUpdateSections);

    s.emit('bookings:join', { day, section: '' });

    return () => {
      try { s.emit('bookings:leave', { day, section: '' }); } catch {}
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('bookings:update', onUpdate);
      s.off('bookings:update-sections', onUpdateSections);
      s.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 一次

  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;
    s.emit('bookings:join', { day, section: '' });
  }, [day]);

  /** 定期輪詢：無論 socket 是否連接都執行，每 5 秒強制重新查詢資料庫 */
  useEffect(() => {
    let timer = null, stopped = false;
    const tick = async () => {
      if (stopped || !mountedRef.current) return;
      // 強制重新查詢資料庫，不使用快取
      await fetchSections({ silent: true });
      await fetchList({ d: day, silent: true });
      timer = setTimeout(tick, REFRESH_MS);
    };
    const resume = () => { 
      if (stopped) {
        stopped = false; 
        tick(); 
      }
    };
    const pause  = () => { 
      stopped = true; 
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const onVis = () => { 
      if (document.hidden) {
        pause();
      } else {
        resume();
      }
    };
    const onFocus = () => {
      if (!stopped && mountedRef.current) {
        // 頁面重新獲得焦點時立即刷新
        fetchSections({ silent: true });
        fetchList({ d: day, silent: true });
      }
      resume();
    };
    const onOnline = resume;

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    // 立即開始輪詢
    resume();
    
    return () => {
      pause();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [day, fetchSections, fetchList]);

  /** 更新（打 API） */
  const updateFlag = async (row, patch) => {
    const run = preserveScroll(async () => {
      const { data } = await axios.patch(`/api/bookings/${row.ID}`, patch);
      if (data?.ok) {
        const oldNumber = Number(row.NUMBER) || 0;
        const newNumber = Number(data.row?.NUMBER) || 0;
        const numberChanged = oldNumber !== newNumber;

        setList((ls) => {
          let next = ls.map((x) => (x.ID === row.ID ? { ...x, ...data.row } : x));
          if (data.pairedRow?.ID != null) {
            const pid = data.pairedRow.ID;
            const idx = next.findIndex((x) => x.ID === pid);
            if (idx >= 0) {
              next = next.map((x) => (x.ID === pid ? { ...x, ...data.pairedRow } : x));
            } else {
              next = [...next, data.pairedRow];
            }
          }
          return next;
        });

        // NUMBER 變更時，需要重新計算 sections（人數統計）
        if (numberChanged || 'sign_written' in patch || 'checked_in' in patch || 'retrieve_sign' in patch) {
          await fetchSections({ silent: true });
          if ('retrieve_sign' in patch) await fetchList({ d: day, silent: true });
        }
      }
    });
    try { await run(); } catch { alert('更新失敗，請重試'); }
  };

  /** === 計數以「人數 NUMBER」為單位 === */
  const sumPeople = (rows) => rows.reduce((s, r) => s + (Number(r.NUMBER) || 0), 0);
  const listALL = list;
  const list1F  = useMemo(() => list.filter(r => isSeatOnFloor(r.SEAT_NAME, '1F')), [list]);
  const list2F  = useMemo(() => list.filter(r => isSeatOnFloor(r.SEAT_NAME, '2F')), [list]);
  const list2FByKey = useMemo(() => {
    const m = {}; TWO_F_SECTIONS.forEach(k => m[k] = []);
    m['夜貓子方案'] = []; // 添加夜貓子方案分區
    for (const r of list) {
      const seatName = String(r.SEAT_NAME || '').trim();
      // 特殊處理：夜貓子方案
      if (seatName === '夜貓子方案') {
        m['夜貓子方案'].push(r);
      } else {
        const k4 = key4Of(seatName).toUpperCase();
        if (m[k4]) m[k4].push(r);
      }
    }
    return m;
  }, [list]);

  /** 目前選擇 view 的資料 */
  const filteredList = useMemo(() => {
    if (view === 'ALL') return listALL;
    if (view === '1F')  return list1F;
    if (view === '2F')  return list2F;
    if (TWO_F_SECTIONS.includes(view)) return list2FByKey[view] || [];
    if (view === '夜貓子方案') return list2FByKey['夜貓子方案'] || [];
    return listALL;
  }, [view, listALL, list1F, list2F, list2FByKey]);

  /** 上方分頁：手機為 全部/1F/2F，2F 才出短字第二列 */
  const Tabs = () => {
    const primary = [
      { key:'ALL', label:'全部', count: sumPeople(listALL) },
      { key:'1F',  label:'1F',  count: sumPeople(list1F)  },
      { key:'2F',  label:'2F',  count: sumPeople(list2F)  },
    ];
    const show2fSubs = view === '2F' || TWO_F_SECTIONS.includes(view) || view === '夜貓子方案';
    const subs = [
      ...TWO_F_SECTIONS.map(k => ({
        key:k,
        label: k.replace('2F-', ''),
        count: sumPeople(list2FByKey[k] || [])
      })),
      ...(list2FByKey['夜貓子方案']?.length ? [{ key: '夜貓子方案', label: '夜貓', count: sumPeople(list2FByKey['夜貓子方案']) }] : []),
    ];
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) {
      return (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {primary.map((it) => {
              const on = (it.key === '2F' && show2fSubs) || view === it.key;
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => setView(it.key)}
                  style={{
                    ...st.topBtn,
                    padding: '8px 6px',
                    justifyContent: 'center',
                    background: on ? '#111' : '#fff',
                    color: on ? '#fff' : '#111',
                    borderColor: on ? '#111' : 'rgba(0,0,0,.18)',
                  }}
                >
                  {it.label} {it.count}
                </button>
              );
            })}
          </div>
          {show2fSubs && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginTop: 6, paddingBottom: 2 }}>
              {subs.map((it) => {
                const on = view === it.key;
                return (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => setView(it.key)}
                    style={{
                      ...st.topBtn,
                      padding: '6px 10px',
                      fontSize: 12,
                      background: on ? '#111' : '#fff',
                      color: on ? '#fff' : '#111',
                    }}
                  >
                    {it.label} {it.count}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const items = [
      ...primary.map((p) => (p.key === 'ALL' ? { ...p, label: '總攬' } : p)),
      ...TWO_F_SECTIONS.map(k => ({
        key:k,
        label: SECTION_NAME_MAP[k] || k,
        count: sumPeople(list2FByKey[k] || [])
      })),
      ...(list2FByKey['夜貓子方案']?.length ? [{ key: '夜貓子方案', label: '夜貓子方案', count: sumPeople(list2FByKey['夜貓子方案']) }] : []),
    ];
    return (
      <div style={st.topTabs}>
        {items.map(it => {
          const isActive = view === it.key;
          return (
            <button
              key={it.key}
              onClick={() => setView(it.key)}
              style={{
                ...st.topBtn,
                background: isActive ? '#111' : '#fff',
                color: isActive ? '#fff' : '#111',
                borderColor: isActive ? '#111' : 'rgba(0,0,0,.18)'
              }}
              title={`${it.label}（${day}；總人數：${it.count}）`}
            >
              <span style={{ fontWeight:900, maxWidth:TAB_MAX_NAME_W, overflow:'hidden', textOverflow:'ellipsis' }}>
                {it.label}
              </span>
              <span style={{
                ...st.countBadge,
                background: isActive ? '#fff' : '#111',
                color: isActive ? '#111' : '#fff'
              }}>
                {it.count}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  /** 列表卡片 */
  const Row = ({ r }) => {
    const startStr     = r.START_HM     || pickHM(r.START_AT);
    const checkinStr   = r.CHECKIN_HM   || pickHM(r.CHECKIN_AT);
    const retrievedStr = r.RETRIEVED_HM || '';

    const isCancelledToday  = r.is_cancelled_today === 1;
    const isRejectedToday   = r.is_rejected_today === 1;
    const isVoidToday       = isCancelledToday || isRejectedToday;
    const isOverdueNeedTake = r.is_overdue_need_retrieve === 1;
    const isCheckedIn       = !!r.CHECKED_IN;
    const isRetrieved       = !!r.SIGN_RETRIEVED_AT;

    // 視覺：取消／拒絕 > 已入場 > 逾時 > 一般
    let bg = '#fff', bdr = '1px solid rgba(0,0,0,.10)', pill = '';
    if (isVoidToday) {
      bg = '#fee2e2';
      bdr = '1px solid #ef4444';
      pill = isRejectedToday ? '今日已拒絕' : '今日臨時取消';
    }
    else if (isCheckedIn) { bg = '#f3f4f6'; bdr = '1px solid #d1d5db'; }
    else if (isOverdueNeedTake) { bg = '#FEF9C3'; bdr = '1px solid #EAB308'; pill = '已超過保留時間'; }

    const infoFont = { fontSize: 18, color:'#111' };

    return (
      <div className="booking-row" style={{
        display:'grid',
        gridTemplateColumns: (typeof window !== 'undefined' && window.innerWidth < 768) ? '1fr' : '120px 1fr auto',
        gap:12, alignItems:'start',
        padding:'12px 14px', borderRadius:12, background:bg, border:bdr
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:8, minWidth:0 }}>
          <div style={{ fontWeight:800, fontSize:20, color:'#111' }}>{startStr}</div>
          <div style={{ fontWeight:800, fontSize:16 }}>人數 {r.NUMBER ?? '-'}</div>
        </div>
        <div style={{ minWidth:0, lineHeight:1.45, wordBreak:'break-word' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#64748b', marginBottom:2 }}>
            預約編號：{String(r.ORDER_ID || r.ORDER_NO || '').trim() || '—'}
          </div>
          <div style={{ fontWeight:900, fontSize:17 }}>
            {r.CUSTOMER_NAME || '（未填姓名）'}{' '}
            {r.PHONE ? (
              <a
                href={`${window.location.pathname.startsWith('/count') ? '/count' : '/admin'}?phone=${encodeURIComponent(r.PHONE)}`}
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                {r.PHONE}
              </a>
            ) : null}
          </div>
          <LevelBadge level={r.DISPLAY_LEVEL} />
          {pill && (
            <span style={{
              fontSize:13, fontWeight:900,
              color: isVoidToday ? '#b91c1c' : '#92400e',
              border:`1.5px solid ${isVoidToday ? '#ef4444' : '#EAB308'}`,
              padding:'2px 8px', borderRadius:999,
              background: isVoidToday ? '#fee2e2' : '#FFFBEB'
            }}>{pill}</span>
          )}
          <div style={{ marginTop:6, fontSize:16 }}>座位：<b>{r.SEAT_NAME || '-'}</b>{r.PAIR_LABEL_UI ? ` · ${r.PAIR_LABEL_UI}` : ''}</div>
          {r.NOTE ? <div style={{ marginTop:4, fontSize:15 }}>備註：{r.NOTE}</div> : null}
        </div>

        {/* 右：操作區 */}
        <div className="booking-row-actions" style={{ display:'grid', gap:8, justifyItems:'start', position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
            <label style={ck.label}>
              <input
                type="checkbox"
                checked={!!r.SIGN_WRITTEN}
                onChange={e => updateFlag(r, { sign_written: e.target.checked })}
                style={ck.input}
              />
              <span>寫預約牌</span>
            </label>
            <label style={ck.label}>
              <input
                type="checkbox"
                checked={!!r.CHECKED_IN}
                onChange={e => updateFlag(r, { checked_in: e.target.checked })}
                style={ck.input}
              />
              <span>已入場</span>
            </label>
          </div>

          {pickHM(r.CHECKIN_AT) !== '-' && (
            <div style={{ fontSize:12, textAlign:'right', color:'#111' }}>入場：{pickHM(r.CHECKIN_AT)}</div>
          )}

          {!r.CHECKED_IN && (
            <>
              {r.is_overdue_need_retrieve === 1 && !r.SIGN_RETRIEVED_AT && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateFlag(r, { retrieve_sign: true }); }}
                  style={{
                    padding:'8px 12px', fontSize:14, fontWeight:900,
                    color:'#fff', background:'#ef4444', border:'1px solid #ef4444',
                    borderRadius:8, cursor:'pointer', position:'relative', zIndex:2
                  }}
                  title="請將已放的預約牌取回（不會變動『寫預約牌』勾選，只會記錄取回時間）"
                >
                  請取回預約牌
                </button>
              )}
              {!!r.SIGN_RETRIEVED_AT && (
                <button
                  type="button"
                  disabled
                  style={{
                    padding:'8px 12px', fontSize:14, fontWeight:900,
                    color:'#fff', background:'#9ca3af', border:'1px solid #9ca3af',
                    borderRadius:8, cursor:'default'
                  }}
                  title={`已於 ${r.RETRIEVED_HM || ''} 取回預約牌`}
                >
                  已取回預約牌
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  /** 上方：日期 + 分頁（單行可水平捲動） */
  return (
    <div className="booking-board-page" style={st.page}>
      <style>{`
        @media (max-width: 768px) {
          .booking-board-page { height: auto !important; min-height: calc(var(--vh, 1vh) * 100); }
          .booking-row { grid-template-columns: 1fr !important; }
          .booking-row-actions { justify-items: start !important; }
          .booking-top-row { flex-wrap: wrap !important; }
        }
      `}</style>
      <div style={st.topBar}>
        <div className="booking-top-row" style={st.topRow}>
          <div style={st.topLabel}>分區（總攬 / 1F / 2F / 2F 各分區）</div>
          <div style={st.dateCtrl}>
            <button style={st.dateBtn}
              onClick={() => setDay(yyyyMMdd(new Date(parseDate(day).getTime() - 86400000)))}
              title="前一天">← 前一天</button>
            <div style={{ fontWeight:1000, fontSize:13, minWidth:88, textAlign:'center' }}>{dayLabel}</div>
            <button style={st.dateBtn} onClick={() => setDay(yyyyMMdd(new Date()))} title="跳到今日">今日</button>
            <button style={st.dateBtn}
              onClick={() => setDay(yyyyMMdd(new Date(parseDate(day).getTime() + 86400000)))}
              title="後一天">後一天 →</button>
          </div>
        </div>
        <Tabs />
      </div>

      <main style={st.main}>
        <div style={st.head}>
          <div style={st.headTitle}>
            查看{day === yyyyMMdd(new Date()) ? '本日' : day}預約：
            {view === 'ALL' ? '總攬'
              : view === '1F' ? '1F'
              : view === '2F' ? '2F'
              : (SECTION_NAME_MAP[view] || view)}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <span style={legend('#ef4444')}>今日臨時取消</span>
            <span style={legend('#EAB308')}>逾時（已超過保留時間）</span>
            <button
              onClick={() => { 
                fetchList({ d: day, silent: false }); 
                fetchSections({ silent: false }); 
              }}
              style={st.refresh}
            >
              重新整理
            </button>
          </div>
        </div>

        {/* 明細：吃滿剩餘高度、只在此滾動 */}
        <div ref={scrollRef} style={st.scrollArea}>
          {loading ? <div style={{ padding:10, fontSize:13 }}>讀取中…</div> :
           err ? <div style={{ padding:10, color:'#b91c1c', fontSize:13 }}>{err}</div> :
           filteredList.length === 0 ? <div style={{ padding:10, color:'#111', fontSize:13 }}>此分頁無預約</div> :
           <div style={{ display:'grid', gap:8 }}>
             {filteredList.map(r => <Row key={r.ID} r={r} />)}
           </div>}
        </div>
      </main>
    </div>
  );
}

/* ===== 樣式（Flex 版型，自動吃滿視窗；只有明細滾動） ===== */
const st = {
  page:{
    height: 'auto',
    minHeight: 'calc(var(--vh, 1vh) * 100)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 10,
    fontFamily: 'system-ui, "Microsoft JhengHei", sans-serif',
    background:'#f6f5f4',
    color:'#111'
  },

  topBar:{ background:'#fff', border:'1px solid rgba(0,0,0,.08)', borderRadius:10, padding:'8px 10px' },
  topRow:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:6 },
  topLabel:{ fontWeight:900, fontSize:13, marginLeft:2 },
  dateCtrl:{ display:'inline-flex', alignItems:'center', gap:6 },
  dateBtn:{ padding:'5px 8px', border:'1px solid rgba(0,0,0,.22)', borderRadius:8, background:'#fff', fontWeight:900, fontSize:12, cursor:'pointer' },

  topTabs:{ display:'flex', gap:6, overflowX:'auto', whiteSpace:'nowrap', paddingBottom:2 },
  topBtn:{
    display:'inline-flex', alignItems:'center', gap:8,
    padding:'8px 10px', borderRadius:9, border:'1px solid rgba(0,0,0,.18)',
    fontSize:13, fontWeight:800, whiteSpace:'nowrap', cursor:'pointer'
  },
  countBadge:{
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    minWidth:20, height:20, padding:'0 6px', borderRadius:999,
    fontSize:11, fontWeight:900
  },

  main:{
    background:'#fff', border:'1px solid rgba(0,0,0,.08)', borderRadius:10,
    padding:10,
    display:'flex', flexDirection:'column', gap:8,
    flex: 1,            // ← 吃滿剩餘高度
    minHeight: 0        // ← 讓內層可縮，避免被內容撐爆
  },
  head:{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingBottom:6, borderBottom:'1px solid rgba(0,0,0,.08)', gap:8, flexWrap:'wrap' },
  headTitle:{ fontSize:15, fontWeight:1000 },
  refresh:{ padding:'6px 10px', borderRadius:8, border:'1px solid rgba(0,0,0,.22)', background:'#fff', fontWeight:900, cursor:'pointer', fontSize:12, color:'#111' },

  scrollArea:{
    flex: 1,            // ← 與 main 搭配，讓此區自動填滿並滾動
    minHeight: 0,
    overflowY:'auto',
    paddingRight:4
  }
};

const ck = {
  label:{ display:'inline-flex', alignItems:'center', gap:10, fontSize:16, fontWeight:900, color:'#111' },
  input:{ width:26, height:26 }
};

const legend = (c) => ({
  fontSize:12, fontWeight:900, color:'#111', background:'#fff', border:`2px solid ${c}`,
  padding:'4px 8px', borderRadius:999
});
